<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-share-variant</v-icon>
            My Shared Links
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              prepend-icon="mdi-plus"
              @click="showCreateDialog = true"
            >
              Create New Share Link
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-4">Loading shares...</p>
            </div>
            
            <div v-else-if="error" class="text-center py-8">
              <v-icon size="64" color="error">mdi-alert-circle</v-icon>
              <p class="text-h6 mt-4 text-error">{{ error }}</p>
              <v-btn 
                color="primary" 
                @click="retryLoad" 
                class="mt-4"
                prepend-icon="mdi-refresh"
              >
                Retry
              </v-btn>
            </div>
            
            <div v-else-if="shareLinks.length === 0" class="text-center py-8">
              <v-icon size="64" color="grey">mdi-share-off</v-icon>
              <p class="text-h6 mt-4">No shared links yet</p>
              <p class="text-body-2">Create your first share link to collaborate with others</p>
            </div>
            
            <v-data-table
              v-else
              :headers="headers"
              :items="shareLinks"
              item-key="id"
              class="elevation-0"
            >
              <template v-slot:item.list_names="{ item }">
                <div class="d-flex align-center flex-wrap">
                  <v-chip
                    v-for="listName in item.list_names"
                    :key="listName"
                    size="small"
                    variant="outlined"
                    color="primary"
                    class="ma-1"
                  >
                    <v-icon start size="small">mdi-format-list-bulleted</v-icon>
                    {{ listName }}
                  </v-chip>
                </div>
              </template>
              
              <template v-slot:item.url="{ item }">
                <div class="d-flex align-center">
                  <v-text-field
                    :model-value="getFullUrl(item.url)"
                    readonly
                    density="compact"
                    hide-details
                    variant="outlined"
                    class="mr-2"
                    style="max-width: 300px;"
                  >
                    <template #append-inner>
                      <v-btn
                        icon="mdi-content-copy"
                        size="small"
                        variant="text"
                        @click="copyToClipboard(getFullUrl(item.url))"
                      ></v-btn>
                    </template>
                  </v-text-field>
                </div>
              </template>
              
              <template v-slot:item.security="{ item }">
                <div class="d-flex flex-wrap gap-1">
                  <v-chip
                    v-if="item.password_hash"
                    size="small"
                    color="warning"
                    variant="outlined"
                  >
                    <v-icon start size="small">mdi-lock</v-icon>
                    Password
                  </v-chip>
                  
                  <v-chip
                    v-if="item.valid_until && isExpired(item.valid_until)"
                    size="small"
                    color="error"
                    variant="tonal"
                  >
                    <v-icon start size="small">mdi-clock-alert</v-icon>
                    EXPIRED
                  </v-chip>
                  
                  <v-chip
                    v-else-if="item.valid_until"
                    size="small"
                    color="info"
                    variant="outlined"
                  >
                    <v-icon start size="small">mdi-clock-outline</v-icon>
                    Expires {{ formatDateTime(item.valid_until) }}
                  </v-chip>
                  
                  <v-chip
                    v-if="!item.password_hash && !item.valid_until"
                    size="small"
                    color="success"
                    variant="outlined"
                  >
                    <v-icon start size="small">mdi-earth</v-icon>
                    Public
                  </v-chip>
                </div>
              </template>
              
              <template v-slot:item.permissions="{ item }">
                <div class="d-flex flex-wrap gap-1">
                  <v-chip
                    v-for="permission in getPermissionLabels(item)"
                    :key="permission"
                    size="x-small"
                    variant="outlined"
                    class="ma-1"
                  >
                    {{ permission }}
                  </v-chip>
                </div>
              </template>
              
              <template v-slot:item.created_at="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
              
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="primary">mdi-share-variant</v-icon>
                  {{ item.name }}
                </div>
              </template>
              
              <template v-slot:item.actions="{ item }">
                <div class="d-flex gap-1">
                  <v-tooltip :text="isExpired(item.valid_until) ? 'Link has expired' : 'Open Link'">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-open-in-new"
                        size="small"
                        variant="text"
                        :color="isExpired(item.valid_until) ? 'grey' : 'primary'"
                        :disabled="isExpired(item.valid_until)"
                        @click="openLink(getFullUrl(item.url))"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                  
                  <v-tooltip :text="isExpired(item.valid_until) ? 'Link has expired' : 'Copy Link'">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-content-copy"
                        size="small"
                        variant="text"
                        :disabled="isExpired(item.valid_until)"
                        @click="copyToClipboard(getFullUrl(item.url))"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                  
                  <v-tooltip text="Edit Lists">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-pencil"
                        size="small"
                        variant="text"
                        color="primary"
                        @click="editShareLists(item)"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                  
                  <v-tooltip text="Delete Share">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-delete"
                        size="small"
                        variant="text"
                        color="error"
                        @click="deleteShareLink(item.id)"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Create Share Link Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600">
      <v-card>
        <v-card-title>Create New Share Link</v-card-title>
        <v-card-text>
          <v-form ref="shareForm" v-model="shareValid">
            <v-text-field
              v-model="shareData.name"
              label="Share Name *"
              :rules="[v => !!v || 'Share name is required']"
              required
              hint="Give this share a descriptive name"
            ></v-text-field>
            
            <v-select
              v-model="shareData.packlist_ids"
              :items="listOptions"
              item-title="display_name"
              item-value="id"
              label="Select Packlists to Share *"
              :rules="[v => (v && v.length > 0) || 'At least one packlist is required']"
              required
              multiple
              chips
              clearable
              hint="You can select multiple lists to share together"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 2"
                  size="small"
                  variant="outlined"
                  closable
                  @click:close="removePacklist(item.raw.id)"
                >
                  {{ item.title }}
                </v-chip>
                <span
                  v-if="index === 2"
                  class="text-grey text-caption align-self-center"
                >
                  (+{{ shareData.packlist_ids.length - 2 }} others)
                </span>
              </template>
            </v-select>
            
            <v-text-field
              v-model="shareData.password"
              label="Password (optional)"
              :type="showPassword ? 'text' : 'password'"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
              hint="Leave empty for public access"
              clearable
            ></v-text-field>
            
            <v-text-field
              v-model="shareData.validUntil"
              label="Valid until (optional)"
              type="datetime-local"
              hint="Leave empty for permanent link"
              clearable
            ></v-text-field>
            
            <v-card class="mt-4" variant="outlined">
              <v-card-title class="text-h6">Permissions</v-card-title>
              <v-card-text>
                <v-checkbox v-model="shareData.can_read" label="Read List (view the list)" disabled></v-checkbox>
                <v-checkbox v-model="shareData.can_check_items" label="Check Items (mark items as completed)"></v-checkbox>
                <v-checkbox v-model="shareData.can_create_items" label="Create Items (add new items to the list)"></v-checkbox>
                <v-checkbox v-model="shareData.can_edit_items" label="Edit Items (modify existing items)"></v-checkbox>
                <v-checkbox v-model="shareData.can_delete_items" label="Delete Items (remove items from the list)"></v-checkbox>
                <v-checkbox v-model="shareData.can_add_groups" label="Add Groups (create new item groups)"></v-checkbox>
                <v-checkbox v-model="shareData.can_update_groups" label="Edit Groups (modify existing groups)"></v-checkbox>
                <v-checkbox v-model="shareData.can_delete_groups" label="Delete Groups (remove groups and move items to general)"></v-checkbox>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelCreate">Cancel</v-btn>
          <v-btn
            @click="createShareLink"
            color="primary"
            :loading="creatingShare"
            :disabled="!shareValid"
          >
            Create Share Link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Edit Share Lists Dialog -->
    <v-dialog v-model="showEditListsDialog" max-width="600">
      <v-card>
        <v-card-title>Edit Shared Lists</v-card-title>
        <v-card-text>
          <v-select
            v-model="editListsData.packlist_ids"
            :items="listOptions"
            item-title="display_name"
            item-value="id"
            label="Select Lists to Share"
            multiple
            chips
            clearable
            hint="Select which lists should be included in this share"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                v-if="index < 3"
                size="small"
                variant="outlined"
                closable
                @click:close="editListsData.packlist_ids.splice(editListsData.packlist_ids.indexOf(item.raw.id), 1)"
              >
                {{ item.title }}
              </v-chip>
              <span
                v-if="index === 3"
                class="text-grey text-caption align-self-center"
              >
                (+{{ editListsData.packlist_ids.length - 3 }} others)
              </span>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelEditLists">Cancel</v-btn>
          <v-btn
            @click="updateShareLists"
            color="primary"
            :disabled="!editListsData.packlist_ids || editListsData.packlist_ids.length === 0"
          >
            Update Lists
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useListStore } from '../stores/list'
import { useAuthStore } from '../stores/auth'
import axios from 'axios'

const listStore = useListStore()
const authStore = useAuthStore()

const loading = ref(true)
const shareLinks = ref([])
const showCreateDialog = ref(false)
const shareValid = ref(false)
const creatingShare = ref(false)
const showPassword = ref(false)
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')
const error = ref(null)

const shareData = ref({
  name: '',
  packlist_ids: [],
  password: '',
  validUntil: '',
  can_read: true,
  can_check_items: false,
  can_create_items: false,
  can_edit_items: false,
  can_delete_items: false,
  can_add_groups: false,
  can_update_groups: false,
  can_delete_groups: false
})

const editingShare = ref(null)
const showEditListsDialog = ref(false)
const editListsData = ref({
  shareId: null,
  packlist_ids: []
})

const headers = [
  { title: 'Share Name', key: 'name', sortable: true },
  { title: 'Lists', key: 'list_names', sortable: false },
  { title: 'Share URL', key: 'url', sortable: false },
  { title: 'Security', key: 'security', sortable: false },
  { title: 'Permissions', key: 'permissions', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: 150 }
]

const listOptions = computed(() => {
  return (listStore.lists || []).map(list => ({
    id: list.id,
    display_name: getPacklistDisplayName(list)
  }))
})

const getPacklistDisplayName = (list) => {
  if (!list) return ''
  const duration = list.duration
  let durationText = ''
  
  if (duration && duration !== 1) {
    if (duration === 7) durationText = ' (1 week)'
    else if (duration === 14) durationText = ' (2 weeks)'
    else if (duration > 7 && duration % 7 === 0) durationText = ` (${Math.floor(duration / 7)} weeks)`
    else durationText = ` (${duration} days)`
  }
  
  return list.name + durationText
}

const loadAllShareLinks = async () => {
  try {
    loading.value = true
    error.value = null
    
    // Ensure we have authentication
    if (!authStore.token) {
      throw new Error('Please log in to view shared links')
    }
    
    // Make sure the auth header is set
    if (!axios.defaults.headers.common['Authorization']) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authStore.token}`
    }
    
    // Use the new multi-list shared links endpoint
    const response = await axios.get('/api/shared-links')
    const rawShareLinks = response.data || []
    
    // Transform the data to include list names
    shareLinks.value = rawShareLinks.map(shareLink => ({
      ...shareLink,
      list_names: shareLink.packlist_ids.map(packlistId => {
        const list = listStore.lists.find(l => l.id === packlistId)
        return list ? getPacklistDisplayName(list) : 'Unknown List'
      })
      // Keep the original URL with HMAC from backend
    }))
    
  } catch (err) {
    let errorMessage = 'Failed to load share links'
    
    if (!authStore.token) {
      errorMessage = 'Please log in to view shared links'
    } else if (err.response?.status === 401) {
      errorMessage = 'Session expired. Please log in again.'
      // Redirect to login after a short delay
      setTimeout(() => {
        authStore.logout()
      }, 2000)
    } else if (err.response?.status === 500) {
      errorMessage = 'Server error. Please try again later.'
    } else if (err.response?.data?.detail) {
      errorMessage = err.response.data.detail
    } else if (err.message) {
      errorMessage = err.message
    }
    
    error.value = errorMessage
  } finally {
    loading.value = false
  }
}


const retryLoad = async () => {
  await loadAllShareLinks()
}

const createShareLink = async () => {
  if (!shareValid.value) return
  
  try {
    creatingShare.value = true
    
    // Convert local datetime to UTC ISO string
    let validUntilUTC = null
    if (shareData.value.validUntil) {
      // datetime-local gives us local time, convert to UTC
      const localDate = new Date(shareData.value.validUntil)
      validUntilUTC = localDate.toISOString() // This converts to UTC with Z suffix
    }
    
    const linkData = {
      name: shareData.value.name,
      packlist_ids: shareData.value.packlist_ids,
      password: shareData.value.password || null,
      valid_until: validUntilUTC,
      can_read: shareData.value.can_read,
      can_check_items: shareData.value.can_check_items,
      can_create_items: shareData.value.can_create_items,
      can_edit_items: shareData.value.can_edit_items,
      can_delete_items: shareData.value.can_delete_items,
      can_add_groups: shareData.value.can_add_groups,
      can_update_groups: shareData.value.can_update_groups,
      can_delete_groups: shareData.value.can_delete_groups
    }
    
    const response = await axios.post('/api/shared-links', linkData)
    
    // Transform and add the new share to the list
    const sharedLinkData = response.data.shared_link
    const newShare = {
      ...sharedLinkData,
      list_names: sharedLinkData.packlist_ids.map(packlistId => {
        const list = listStore.lists.find(l => l.id === packlistId)
        return list ? getPacklistDisplayName(list) : 'Unknown List'
      }),
      url: response.data.url  // Use the URL with HMAC from backend
    }
    
    shareLinks.value.push(newShare)
    
    showSnackbar('Share link created successfully!', 'success')
    cancelCreate()
    
  } catch (error) {
    showSnackbar(error.response?.data?.detail || 'Failed to create share link', 'error')
  } finally {
    creatingShare.value = false
  }
}

const deleteShareLink = async (linkId) => {
  if (!confirm('Delete this share link? It will no longer be accessible.')) return
  
  try {
    await axios.delete(`/api/shared-links/${linkId}`)
    shareLinks.value = shareLinks.value.filter(link => link.id !== linkId)
    showSnackbar('Share link deleted', 'success')
  } catch (error) {
    showSnackbar('Failed to delete share link', 'error')
  }
}

const editShareLists = (shareLink) => {
  editingShare.value = shareLink
  editListsData.value = {
    shareId: shareLink.id,
    packlist_ids: [...shareLink.packlist_ids]
  }
  showEditListsDialog.value = true
}

const updateShareLists = async () => {
  try {
    const updateData = {
      packlist_ids: editListsData.value.packlist_ids
    }
    
    await axios.put(`/api/shared-links/${editListsData.value.shareId}/lists`, updateData)
    
    // Update the local data
    const shareIndex = shareLinks.value.findIndex(s => s.id === editListsData.value.shareId)
    if (shareIndex !== -1) {
      shareLinks.value[shareIndex].packlist_ids = editListsData.value.packlist_ids
      shareLinks.value[shareIndex].list_names = editListsData.value.packlist_ids.map(packlistId => {
        const list = listStore.lists.find(l => l.id === packlistId)
        return list ? getPacklistDisplayName(list) : 'Unknown List'
      })
    }
    
    showSnackbar('Share lists updated successfully!', 'success')
    cancelEditLists()
    
  } catch (error) {
    showSnackbar(error.response?.data?.detail || 'Failed to update share lists', 'error')
  }
}

const cancelEditLists = () => {
  editingShare.value = null
  editListsData.value = {
    shareId: null,
    packlist_ids: []
  }
  showEditListsDialog.value = false
}

const removePacklist = (packlistId) => {
  const index = shareData.value.packlist_ids.indexOf(packlistId)
  if (index > -1) {
    shareData.value.packlist_ids.splice(index, 1)
  }
}

const cancelCreate = () => {
  shareData.value = {
    name: '',
    packlist_ids: [],
    password: '',
    validUntil: '',
    can_read: true,
    can_check_items: false,
    can_create_items: false,
    can_edit_items: false,
    can_delete_items: false,
    can_add_groups: false,
    can_update_groups: false,
    can_delete_groups: false
  }
  showCreateDialog.value = false
}

const getFullUrl = (relativeUrl) => {
  return window.location.origin + relativeUrl
}

const openLink = (url) => {
  window.open(url, '_blank')
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    showSnackbar('Link copied to clipboard!', 'success')
  } catch (error) {
    showSnackbar('Failed to copy link', 'error')
  }
}

const getPermissionLabels = (sharedLink) => {
  const permissions = []
  if (sharedLink.can_read) permissions.push('Read List')
  if (sharedLink.can_check_items) permissions.push('Check Items')
  if (sharedLink.can_create_items) permissions.push('Create Items')
  if (sharedLink.can_edit_items) permissions.push('Edit Items')
  if (sharedLink.can_delete_items) permissions.push('Delete Items')
  if (sharedLink.can_add_groups) permissions.push('Add Groups')
  if (sharedLink.can_update_groups) permissions.push('Edit Groups')
  if (sharedLink.can_delete_groups) permissions.push('Delete Groups')
  return permissions
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString()
}

const isExpired = (dateString) => {
  if (!dateString) return false
  // Parse the UTC timestamp correctly (backend returns dates with 'Z' suffix)
  const expiryDate = new Date(dateString)
  const currentDate = new Date()
  return expiryDate <= currentDate
}

const showSnackbar = (text, color = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

onMounted(async () => {
  // Ensure auth is initialized first
  authStore.initializeAuth()
  
  // Wait a moment for auth to initialize if we have a token but no user yet
  if (authStore.token && !authStore.user) {
    await new Promise(resolve => setTimeout(resolve, 200))
  }
  
  // Load lists first (needed for create dialog)
  try {
    await listStore.fetchLists()
  } catch (err) {
    // If lists fail to load, we might have auth issues
    error.value = 'Failed to load data. Please refresh or log in again.'
    loading.value = false
    return
  }
  
  // Then load share links
  await loadAllShareLinks()
})
</script>

<style scoped>
.v-data-table {
  border-radius: 8px;
}
</style>